import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useOrg } from "../../auth/OrgContext";
import LoginForm from "../../components/LoginForm";
import { Spinner, Callout } from "@blueprintjs/core";

const API_BASE =
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";

export default function AcceptInvite() {
  const { token } = useParams();
  const { idToken, refreshAuth } = useAuth();
  const { setCurrentOrg } = useOrg();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string>("");
  const [accountExists, setAccountExists] = useState<boolean>(false);

  const [name, setName] = useState("");
  const [password, setPassword] = useState(""); // fixed unterminated string
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/invites/${token}`)
      .then(async (res) => {
        const data = await res.json();
        if (res.ok) {
          setOrgName(data.orgName);
          setAccountExists(data.accountExists);
        } else {
          setError(data.error || "Invalid invite");
        }
      })
      .catch(() => setError("Network error"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAcceptExisting = async () => {
    if (!token || !idToken) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/invites/${token}/accept`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = await res.json();
      if (res.ok) {
        if (data.org) {
          setCurrentOrg({ id: data.org.id, name: data.org.name });
        }
        // Try to refresh auth, but navigate regardless to avoid getting stuck
        refreshAuth().finally(() => {
          navigate("/dashboard");
        });
      } else {
        setSubmitError(data.error || "Failed to accept invite");
      }
    } catch {
      setSubmitError("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/invites/${token}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, password }),
      });
      const data = await res.json();
      if (res.ok) {
        sessionStorage.setItem("id_token", data.token);
        sessionStorage.setItem("refresh_token", data.refreshToken);
        setCurrentOrg({ id: data.org.id, name: data.org.name });
        // Try to refresh auth, but navigate regardless to avoid getting stuck
        refreshAuth().finally(() => {
          navigate("/dashboard");
        });
      } else {
        setSubmitError(data.error || "Failed to register");
      }
    } catch {
      setSubmitError("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem" }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "2rem" }}>
        <Callout intent="danger">{error}</Callout>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 400, margin: "2rem auto" }}>
      <h2>Join {orgName}</h2>
      {accountExists ? (
        idToken ? (
          <div>
            <button onClick={handleAcceptExisting} disabled={submitting}>
              {submitting ? "Joining..." : "Accept Invitation"}
            </button>
            {submitError && <p style={{ color: "red" }}>{submitError}</p>}
          </div>
        ) : (
          <div>
            <p>Please log in to accept this invitation.</p>
            <LoginForm />
          </div>
        )
      ) : (
        <form onSubmit={handleRegister}>
          <div style={{ marginBottom: "0.5rem" }}>
            <input
              type="text"
              placeholder="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <input
              type="password"
              placeholder="Choose a Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" disabled={submitting}>
            {submitting ? "Creating Account..." : "Create Account & Join"}
          </button>
          {submitError && <p style={{ color: "red" }}>{submitError}</p>}
        </form>
      )}
    </div>
  );
}
